import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Answers } from '../lib/visibility';

/**
 * Détecte l'URL "source" d'un lead :
 *   - si on est dans une iframe (embed), on préfère le document parent (referrer)
 *   - sinon, l'URL courante
 * Permet aux utilisateurs de savoir sur quel site tiers leur générateur a été rempli.
 */
function detectSource(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        if (window.top !== window.self && document.referrer) {
            return document.referrer;
        }
    } catch {
        if (document.referrer) return document.referrer;
    }
    return window.location.href;
}

export interface LeadContact {
    name: string;
    email: string;
    phone: string;
    company?: string;
    message?: string;
}

interface SubmitLeadArgs {
    organizationId: string;
    generatorId: string;
    answers: Answers;
    contact: LeadContact;
    estimatedTotal: number;
    source?: string;
}

interface UseSubmitLeadResult {
    submit: (args: SubmitLeadArgs) => Promise<boolean>;
    submitting: boolean;
    error: string | null;
}

/**
 * Envoie une submission (lead) vers Supabase en s'appuyant sur la policy RLS
 * `public_insert_submissions` (insertion anonyme autorisée uniquement pour
 * un generator publié, cf. `public_access.sql`).
 */
export function useSubmitLead(): UseSubmitLeadResult {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = useCallback(async (args: SubmitLeadArgs): Promise<boolean> => {
        setSubmitting(true);
        setError(null);

        const payload = {
            organization_id: args.organizationId,
            generator_id: args.generatorId,
            payload: args.answers as unknown as Record<string, unknown>,
            contact_name: args.contact.name || null,
            contact_email: args.contact.email || null,
            contact_phone: args.contact.phone || null,
            estimated_total: Number.isFinite(args.estimatedTotal) ? args.estimatedTotal : null,
            status: 'new',
            source: args.source ?? detectSource(),
        };

        const { error: err } = await supabase.from('submissions').insert(payload);

        if (err) {
            setError(err.message);
            setSubmitting(false);
            return false;
        }
        setSubmitting(false);
        return true;
    }, []);

    return { submit, submitting, error };
}
